/**
 * @file QuotationForm.jsx
 * @description Formulario de cotización alineado con la identidad visual de AGDYSA.
 * Usa una paleta clara y acentos en verde neón para una integración orgánica.
 * @author JEMAprod-Tecnologia (2026)
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle, Loader2, ClipboardList, Building2, MapPin, Trash2, ShoppingCart } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import options from "../data/quotationOptions.json";
import endpoints from "../config/endpoints.json";
import { functions } from "../firebase/config";
import { useQuotation } from "../context/QuotationContext";
import PrivacyConsentCheckbox from "./PrivacyConsentCheckbox";

/**
 * Sub-componente para los títulos de sección.
 * Ajustado a la línea gráfica: Texto pequeño, negrita y línea de acento.
 */
const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 mb-6 mt-12 border-b border-brand-soft pb-4">
    <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center text-brand-primary">
      <Icon size={18} />
    </div>
    <h3 className="text-brand-primary font-display font-black uppercase tracking-[0.2em] text-[11px]">{children}</h3>
  </div>
);

const QuotationForm = () => {
  /** Principal: CRM (match cliente / turno vendedora + email a vendedora). */
  const CRM_GAS_URL = endpoints.crmLeadForm?.gasUrl || "";
  /** Respaldo: Sheet + info@agdysa.com (sin asignación 1 a 1). */
  const LEGACY_GAS_URL = endpoints.quotationForm?.gasUrl || "";
  const [status, setStatus] = useState("idle");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const { selectedItems, removeItem, clearBasket } = useQuotation();

  const [formData, setFormData] = useState({
    empresa: "", contacto: "", cargo: "", telefono: "", email: "",
    ubicacionGeneral: "", rubro: "", rubroOtro: "", servicios: [],
    lugarServicio: "", lugarDetalle: "", prioridad: "",
    preferenciaContacto: "", comentarios: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (servicio) => {
    setFormData(prev => {
      const exists = prev.servicios.includes(servicio);
      return {
        ...prev,
        servicios: exists 
          ? prev.servicios.filter(s => s !== servicio)
          : [...prev.servicios, servicio]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    
    const serviciosDelCarrito = selectedItems.map(item => item.name);
    const todosLosServicios = [...new Set([...serviciosDelCarrito, ...formData.servicios])];

    const dataToSend = {
      ...formData,
      servicios: todosLosServicios.join(" | "),
      tipoFormulario: "COTIZACIÓN_DETALLADA",
      website_url: "",
      privacyConsent: true,
      privacyConsentAt: new Date().toISOString(),
    };

    try {
      if (!CRM_GAS_URL) {
        console.warn(
          "[Cotización] crmLeadForm.gasUrl vacío: no habrá match 1 a 1 ni email a vendedora."
        );
      }
      // 1) Cloud Function → GAS CRM (LISTA si cliente existe / TURNO 1 a 1 si es nuevo)
      // 2) Espejo Firestore + opcional legacy a info@
      const submitLead = httpsCallable(functions, "submitWebsiteLead");
      await submitLead({
        ...dataToSend,
        forwardGasUrl: CRM_GAS_URL,
        legacyGasUrl: LEGACY_GAS_URL,
      });
      
      setStatus("success");
      clearBasket();
      setPrivacyAccepted(false);
      setFormData({
        empresa: "", contacto: "", cargo: "", telefono: "", email: "",
        ubicacionGeneral: "", rubro: "", rubroOtro: "", servicios: [],
        lugarServicio: "", lugarDetalle: "", prioridad: "",
        preferenciaContacto: "", comentarios: ""
      });
    } catch (error) {
      console.error("[AUDITORÍA] Error en envío de cotización:", error);
      try {
        if (!LEGACY_GAS_URL) throw error;
        const response = await fetch(LEGACY_GAS_URL, {
          method: "POST",
          cache: "no-cache",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify(dataToSend),
          redirect: "follow",
        });
        const data = await response.json();
        if (data.result !== "success") {
          throw new Error(data.message || "Legado no aceptó la cotización");
        }
        setStatus("success");
        clearBasket();
        setPrivacyAccepted(false);
        setFormData({
          empresa: "", contacto: "", cargo: "", telefono: "", email: "",
          ubicacionGeneral: "", rubro: "", rubroOtro: "", servicios: [],
          lugarServicio: "", lugarDetalle: "", prioridad: "",
          preferenciaContacto: "", comentarios: ""
        });
      } catch (fallbackErr) {
        console.error("[AUDITORÍA] Fallback legado falló:", fallbackErr);
        setStatus("error");
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-8 md:p-16 rounded-[60px] text-brand-dark shadow-[0_40px_100px_rgba(0,0,0,0.05)] border border-brand-soft">
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-20">
            <div className="w-20 h-20 bg-brand-soft rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-brand-primary" size={40} />
            </div>
            <h2 className="text-4xl font-display font-black mb-4 tracking-tighter">¡Solicitud Procesada!</h2>
            <p className="text-brand-dark/60 text-lg max-w-sm mx-auto">Nuestro departamento técnico revisará los requerimientos de su empresa.</p>
            <button onClick={() => setStatus("idle")} className="mt-10 bg-brand-primary text-white px-8 py-3 rounded-full font-bold text-xs uppercase tracking-widest hover:bg-brand-dark transition-all">Nueva Solicitud</button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="text-left">
            
            {/* RESUMEN DEL CARRITO (TEMA CLARO) */}
            {selectedItems.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: -20 }} 
                animate={{ opacity: 1, y: 0 }}
                className="bg-brand-soft/30 border border-brand-soft rounded-[30px] p-8 mb-12 shadow-sm"
              >
                <div className="flex items-center gap-3 mb-6">
                  <ShoppingCart className="text-brand-primary" size={18} />
                  <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-primary">
                    Items desde el catálogo
                  </h4>
                </div>
                <div className="flex flex-wrap gap-3">
                  {selectedItems.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-3 bg-white border border-brand-soft text-brand-primary px-5 py-2.5 rounded-2xl text-xs font-bold shadow-sm"
                    >
                      {item.name}
                      <button 
                        type="button" 
                        onClick={() => removeItem(item.id)}
                        className="text-brand-dark/20 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <SectionTitle icon={Building2}>1. Información General</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-2">
                <label className="text-[10px] uppercase text-brand-dark/40 font-black tracking-widest ml-1">Empresa</label>
                <input required name="empresa" value={formData.empresa} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none focus:border-brand-primary transition-all font-medium" placeholder="Nombre legal" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase text-brand-dark/40 font-black tracking-widest ml-1">Contacto</label>
                <input required name="contacto" value={formData.contacto} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none focus:border-brand-primary transition-all font-medium" placeholder="Nombre completo" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase text-brand-dark/40 font-black tracking-widest ml-1">Cargo</label>
                <input name="cargo" value={formData.cargo} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none focus:border-brand-primary transition-all font-medium" placeholder="Puesto en la organización" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase text-brand-dark/40 font-black tracking-widest ml-1">Teléfono</label>
                <input required name="telefono" value={formData.telefono} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none focus:border-brand-primary transition-all font-medium" placeholder="WhatsApp o Directo" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-[10px] uppercase text-brand-dark/40 font-black tracking-widest ml-1">Email Corporativo</label>
                <input required type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none focus:border-brand-primary transition-all font-medium" placeholder="ejemplo@empresa.com" />
              </div>
            </div>

            <SectionTitle icon={MapPin}>2. Rubro y Ubicación</SectionTitle>
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <select required name="rubro" value={formData.rubro} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none appearance-none font-bold text-brand-primary cursor-pointer">
                  <option value="">Seleccione Rubro</option>
                  {options.rubros.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {formData.rubro === "Otro" && (
                  <input placeholder="Especifique rubro" name="rubroOtro" value={formData.rubroOtro} onChange={handleInputChange} className="w-full bg-white border border-brand-primary p-5 rounded-2xl outline-none shadow-sm" />
                )}
              </div>
              <input placeholder="Distrito y Municipio ubica la Planta o Sede a visitar" name="ubicacionGeneral" value={formData.ubicacionGeneral} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none focus:border-brand-primary font-medium" />
            </div>

            <SectionTitle icon={ClipboardList}>3. Servicios a Cotizar</SectionTitle>
            <div className="grid gap-4">
              {options.servicios.map((s, i) => {
                const isSelectedInCart = selectedItems.some(item => item.name === s);
                return (
                  <label key={i} className={`flex items-start gap-5 p-6 rounded-3xl cursor-pointer transition-all border ${isSelectedInCart ? 'bg-brand-soft/20 border-brand-accent shadow-sm' : 'bg-surface border-transparent hover:border-brand-soft'}`}>
                    <input 
                      type="checkbox" 
                      checked={formData.servicios.includes(s) || isSelectedInCart} 
                      disabled={isSelectedInCart}
                      onChange={() => handleCheckboxChange(s)} 
                      className="mt-1 w-6 h-6 accent-brand-primary cursor-pointer" 
                    />
                    <span className={`text-[15px] leading-snug font-medium ${isSelectedInCart ? 'text-brand-primary' : 'text-brand-dark/70'}`}>
                      {s}
                    </span>
                  </label>
                );
              })}
            </div>

            <SectionTitle icon={MapPin}>4. Logística</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <select required name="lugarServicio" value={formData.lugarServicio} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none appearance-none font-bold text-brand-primary">
                <option value="">¿Donde requiere el servicio?</option>
                {options.lugares.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              {(formData.lugarServicio.includes("empresa") || formData.lugarServicio.includes("sucursales")) && (
                <input required placeholder="¿Dirección exacta?" name="lugarDetalle" value={formData.lugarDetalle} onChange={handleInputChange} className="w-full bg-white border border-brand-primary p-5 rounded-2xl outline-none shadow-sm" />
              )}
              <select required name="prioridad" value={formData.prioridad} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none appearance-none font-medium">
                <option value="">¿Para cuándo lo necesita?</option>
                {options.tiempos.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select required name="preferenciaContacto" value={formData.preferenciaContacto} onChange={handleInputChange} className="w-full bg-surface border border-brand-soft p-5 rounded-2xl outline-none appearance-none font-medium">
                <option value="">Preferencia de contacto</option>
                {options.metodosContacto.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="mt-10 space-y-4">
              <label className="text-[10px] uppercase text-brand-dark/40 font-black tracking-widest ml-1">Observaciones para el presupuesto</label>
              <textarea name="comentarios" value={formData.comentarios} onChange={handleInputChange} rows="4" className="w-full bg-surface border border-brand-soft p-6 rounded-3xl outline-none focus:border-brand-primary resize-none font-medium" placeholder="Escriba aquí cualquier detalle técnico adicional..."></textarea>
            </div>

            <PrivacyConsentCheckbox
              id="quotation-privacy"
              checked={privacyAccepted}
              onChange={setPrivacyAccepted}
              className="mt-8"
            />

            <button 
              disabled={status === "loading" || !privacyAccepted}
              className="w-full mt-8 bg-brand-primary text-white py-6 rounded-3xl font-display font-black uppercase tracking-[0.2em] flex items-center justify-center gap-4 hover:bg-brand-dark shadow-2xl shadow-brand-primary/20 transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
            >
              {status === "loading" ? <Loader2 className="animate-spin" /> : <><Send size={20} /> Generar Solicitud de Presupuesto</>}
            </button>

          </form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuotationForm;